/* =============================================================================
 * coachPageStyle — the one page container every coach surface uses (KGH-795)
 * -----------------------------------------------------------------------------
 * Copied, not approximated, from the house standard: src/pages/Dashboard.tsx
 * and src/pages/CoachPlayers.tsx both open their page with exactly this box —
 * full width up to 1600 with a 24px gutter (16 on a phone), centred, in the
 * sans stack.
 *
 * Every other coach page had picked its own number — 1080, 1180, 820, 720 —
 * so no two coach surfaces were the same width, and on a 1920 screen the
 * content column stopped around 1370px and left ~550px of dead space down the
 * right-hand side. Owner's words: "'on brand' ui would be full width".
 *
 * WHAT THIS IS NOT. This is the PAGE. A centred single-purpose panel — a
 * sign-up card, an access refusal, a load failure — keeps its own narrow
 * measure INSIDE this container, exactly the way CoachPlayers' AccessPanel
 * does (maxWidth 560, margin '24px auto'). Widening the page does not widen
 * those, and it must not: a refusal stretched across 1600px is not a panel,
 * it is a billboard.
 * ========================================================================== */
import type { CSSProperties } from 'react';
import { font } from '../../styles/tokens';

/** The page-level container. Same object as Dashboard.tsx / CoachPlayers.tsx. */
export const coachPageStyle = (isMobile: boolean): CSSProperties => ({
  padding: isMobile ? '16px' : '24px',
  maxWidth: 1600,
  margin: '0 auto',
  fontFamily: font.sans,
});

/**
 * The measure for running prose — a page lede, a section description — when it
 * sits in the 1600px column. Expressed in `ch` on purpose: it is a typographic
 * decision that scales with the type, rather than a pixel cap that happens to
 * fight whatever column it lands in and leaves the sentence looking
 * hand-broken. 80ch is the top of the comfortable reading range, and keeps
 * these one- and two-sentence ledes to two lines instead of three ragged ones.
 *
 * A caption inside a card, a hint under a field or a cell in a row does NOT use
 * this: those containers really are narrow, and their caps are correct.
 *
 * WHY 130 AND NOT 80. 80ch was the first answer and it was still the bug: at the
 * lede's 15px it resolves to ~720px inside the 1648px page, so the sentence broke
 * at 44% of the column with 900px of nothing beside it — which is what "fake word
 * wrap" meant in the first place, just less of it. 130ch lands at ~1170px, about
 * 71% of the column, which reads as a chosen measure rather than a stranded one,
 * and it is the same rendered width the admin coach desk arrived at independently.
 * Longer than the 45-75ch a book would use, and deliberately: these are one- and
 * two-sentence page ledes, not body copy, and the owner's brief was full width.
 */
export const PROSE_MEASURE = '130ch';

/**
 * The measure for a centred single-purpose panel sitting inside the page
 * container — an access refusal or a load failure. Matches CoachPlayers'
 * AccessPanel so the coach sees the same shape whichever page refuses them.
 */
export const PANEL_MEASURE = 560;

export default coachPageStyle;
